// src/components/Campaigns.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLockBodyScroll } from '../hooks/useLockBodyScroll';
import ConfirmDialog from './ConfirmDialog';

function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [generatedLink, setGeneratedLink] = useState('');
  
  const [formError, setFormError] = useState('');
  const [copyFeedback, setCopyFeedback] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });

  const [formData, setFormData] = useState({
    name: '',
    advertiser_id: '',
    offer_url: '',
    status: 'active',
    cpc: '',
    campaign_group: ''
  });

  useLockBodyScroll(showModal || showLinksModal);

  useEffect(() => {
    fetchCampaigns();
    fetchAdvertisers();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get('/campaigns');
      setCampaigns([...response.data.campaigns].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvertisers = async () => {
    try {
      const response = await axios.get('/advertisers');
      setAdvertisers(response.data.advertisers);
    } catch (error) {
      console.error('Failed to fetch advertisers:', error);
    }
  };

  // Logic to auto-fill Offer URL from Campaign Template
  const handleAdvertiserChange = (e) => {
    const selectedId = e.target.value;
    
    // Find selected advertiser
    const advertiser = advertisers.find(adv => adv.id === selectedId);
    
    // Update state
    setFormData(prev => ({
      ...prev,
      advertiser_id: selectedId,
      // If found and has template, auto-fill. Otherwise keep existing or empty.
      offer_url: (advertiser && advertiser.campaign_template) 
        ? advertiser.campaign_template 
        : prev.offer_url
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCampaign) {
        await axios.put(`/campaigns/${selectedCampaign.id}`, formData);
      } else {
        await axios.post('/campaigns', formData);
      }
      setShowModal(false);
      resetForm();
      fetchCampaigns();
    } catch (error) {
      setFormError(error.response?.data?.error || 'Operation failed');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      advertiser_id: '',
      offer_url: '',
      status: 'active',
      cpc: '',
      campaign_group: ''
    });
    setSelectedCampaign(null);
  };

  const handleGenerateLink = async (campaign) => {
    try {
      const response = await axios.get(`/campaigns/${campaign.id}/tracking-link`);
      setGeneratedLink(response.data.tracking_link);
      setSelectedCampaign(campaign);
      setShowLinksModal(true);
    } catch (error) {
      setFormError('Failed to generate link');
    }
  };

  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(generatedLink);
      } else {
        const ta = document.createElement('textarea');
        ta.value = generatedLink;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopyFeedback('Copied!');
    } catch {
      setCopyFeedback('Failed to copy');
    }
    setTimeout(() => setCopyFeedback(''), 2000);
  };

  const handleDelete = (id) => setConfirmDialog({ open: true, id });
  const performDelete = async () => {
    try {
      await axios.delete(`/campaigns/${confirmDialog.id}`);
      fetchCampaigns();
    } catch { setFormError('Failed to delete campaign'); }
    setConfirmDialog({ open: false, id: null });
  };

  const handleEdit = (campaign) => {
    setSelectedCampaign(campaign);
    setFormData({
      name: campaign.name,
      advertiser_id: campaign.advertiser_id,
      offer_url: campaign.offer_url,
      status: campaign.status,
      cpc: campaign.cpc || '',
      campaign_group: campaign.campaign_group || ''
    });
    setFormError('');
    setShowModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Campaigns</h1>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800"
        >
          + New Campaign
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : campaigns.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            No campaigns yet. Tap "+ New Campaign" to create one.
          </div>
        ) : (
          <>
            {/* Mobile: card list */}
            <ul className="sm:hidden divide-y divide-gray-200">
              {campaigns.map((campaign) => (
                <li key={campaign.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">{campaign.name}</div>
                      <div className="text-sm text-gray-500 truncate">{campaign.advertiser_name}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                        {campaign.campaign_group && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium">
                            {campaign.campaign_group}
                          </span>
                        )}
                        <span className="text-gray-500">CPC ${parseFloat(campaign.cpc || 0).toFixed(4)}</span>
                      </div>
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 text-xs leading-5 font-semibold rounded-full ${
                      campaign.status === 'active' ? 'bg-green-100 text-green-800'
                      : campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                    }`}>
                      {campaign.status}
                    </span>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => handleGenerateLink(campaign)}
                      className="flex-1 min-h-[44px] px-3 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-md active:bg-green-100"
                    >
                      Get Link
                    </button>
                    <button
                      onClick={() => handleEdit(campaign)}
                      className="flex-1 min-h-[44px] px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md active:bg-blue-100"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(campaign.id)}
                      className="flex-1 min-h-[44px] px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-md active:bg-red-100"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/* Desktop: existing table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Advertiser</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CPC</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {campaign.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {campaign.advertiser_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {campaign.campaign_group ? (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            {campaign.campaign_group}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                          campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${parseFloat(campaign.cpc || 0).toFixed(4)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                        <button
                          onClick={() => handleGenerateLink(campaign)}
                          className="text-green-600 hover:text-green-900 active:text-green-700"
                        >
                          Get Link
                        </button>
                        <button
                          onClick={() => handleEdit(campaign)}
                          className="text-blue-600 hover:text-blue-900 active:text-blue-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(campaign.id)}
                          className="text-red-600 hover:text-red-900 active:text-red-700"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 pt-[env(safe-area-inset-top)]"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90dvh] overflow-y-auto overscroll-contain"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4">
              {selectedCampaign ? 'Edit Campaign' : 'New Campaign'}
            </h2>
            {formError && (
              <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800">{formError}</div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Campaign Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Advertiser</label>
                <select
                  required
                  value={formData.advertiser_id}
                  onChange={handleAdvertiserChange} // UPDATED: Trigger auto-fill
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  disabled={!!selectedCampaign}
                >
                  <option value="">Select Advertiser</option>
                  {advertisers.map(adv => (
                    <option key={adv.id} value={adv.id}>{adv.name}</option>
                  ))}
                </select>
                {selectedCampaign && (
                  <p className="mt-1 text-xs text-gray-400">Advertiser cannot be changed when editing.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Offer URL</label>
                <input
                  type="url"
                  inputMode="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  required
                  value={formData.offer_url}
                  onChange={(e) => setFormData({ ...formData, offer_url: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
                  placeholder="https://…/offer?click_id={click_id}"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must include <code>{'{click_id}'}</code> placeholder.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Cost Per Click (CPC)</label>
                <input
                  type="number"
                  step="0.0001"
                  inputMode="decimal"
                  value={formData.cpc}
                  onChange={(e) => setFormData({ ...formData, cpc: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="0.0500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Campaign Group <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.campaign_group}
                  onChange={(e) => setFormData({ ...formData, campaign_group: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="e.g. weight-loss-offer"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Campaigns sharing the same group name share IP blocking rules across advertisers.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                </select>
              </div>

              <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-white border-t flex justify-end gap-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="min-h-[44px] px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 active:bg-blue-800"
                >
                  {selectedCampaign ? 'Update Campaign' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Links Modal */}
      {showLinksModal && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4"
          onClick={() => setShowLinksModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 overscroll-contain"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-4">Tracking Link</h3>
            <div className="p-3 bg-gray-50 rounded border font-mono text-sm break-all mb-4">
              {generatedLink}
            </div>
            <div className="flex justify-between items-center">
              <button
                onClick={handleCopyLink}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
              >
                {copyFeedback || 'Copy Link'}
              </button>
              <button
                onClick={() => setShowLinksModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title="Delete campaign?"
        message="This cannot be undone."
        confirmLabel="Delete"
        danger
        onConfirm={performDelete}
        onCancel={() => setConfirmDialog({ open: false, id: null })}
      />
    </div>
  );
}

export default Campaigns;